import axios from "axios";

export const API_URL = (
  process.env.REACT_APP_API_URL ||
  "/api"
).replace(/\/+$/, "");

const sessionKey = (company) =>
  `netledger_pos:${company}`;

export function getPosSession(company) {
  if (!company) return null;

  try {
    const value = sessionStorage.getItem(
      sessionKey(company)
    );

    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

export function savePosSession(company, data) {
  if (
    !company ||
    !data?.access_token ||
    data.app !== "pos" ||
    data.subdomain !== company
  ) {
    throw new Error(
      "No fue posible guardar la sesión POS."
    );
  }

  sessionStorage.setItem(
    sessionKey(company),
    JSON.stringify(data)
  );
}

export function clearPosSession(
  company,
  expectedToken
) {
  if (!company) return;

  // Una petición antigua no debe borrar una sesión nueva.
  if (
    expectedToken &&
    getPosSession(company)?.access_token !== expectedToken
  ) {
    return;
  }

  sessionStorage.removeItem(
    sessionKey(company)
  );
}

export function getPosHeaders(company) {
  const token =
    getPosSession(company)?.access_token;

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
}

export function getPosExpiration(company) {
  const session = getPosSession(company);

  if (!session?.access_token) {
    return null;
  }

  // El exp del JWT corresponde al vencimiento
  // que también valida el servidor.
  try {
    const encoded =
      session.access_token.split(".")[1];

    const base64 = encoded
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const padded = base64.padEnd(
      Math.ceil(base64.length / 4) * 4,
      "="
    );

    const payload = JSON.parse(
      window.atob(padded)
    );

    if (
      typeof payload.exp === "number" &&
      Number.isFinite(payload.exp)
    ) {
      return payload.exp * 1000;
    }
  } catch {
    // Se intenta con la fecha de la respuesta.
  }

  const expiresAt = Date.parse(
    session.expires_at || ""
  );

  return Number.isFinite(expiresAt)
    ? expiresAt
    : null;
}

export const api = axios.create({
  baseURL: API_URL,
});

function getCurrentCompany() {
  const segment = window.location.pathname
    .split("/")
    .filter(Boolean)[0];

  if (!segment) return null;

  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

function isBackendRequest(config) {
  try {
    const backend = new URL(
      API_URL,
      window.location.origin
    );

    const resolvedBase = new URL(
      config.baseURL || API_URL,
      window.location.origin
    );
    const requestUrl = new URL(
      (config.url || "").replace(/^\/+/, ""),
      `${resolvedBase.href.replace(/\/+$/, "")}/`
    );

    const backendPath =
      backend.pathname.replace(/\/+$/, "");

    return (
      requestUrl.origin === backend.origin &&
      (
        !backendPath ||
        requestUrl.pathname === backendPath ||
        requestUrl.pathname.startsWith(
          `${backendPath}/`
        )
      )
    );
  } catch {
    return false;
  }
}

function getAuthorization(config) {
  const headers = config?.headers;

  if (typeof headers?.get === "function") {
    return headers.get("Authorization");
  }

  return (
    headers?.Authorization ||
    headers?.authorization ||
    ""
  );
}

function handleUnauthorized(error) {
  if (
    error.response?.status !== 401 ||
    !error.config ||
    !isBackendRequest(error.config)
  ) {
    return Promise.reject(error);
  }

  const company = getCurrentCompany();
  const session = getPosSession(company);

  const authorization = getAuthorization(
    error.config
  );

  // Ignora logins fallidos, llamadas públicas y tokens
  // administrativos o pertenecientes a otra sesión.
  if (
    session?.access_token &&
    authorization ===
      `Bearer ${session.access_token}`
  ) {
    clearPosSession(
      company,
      session.access_token
    );

    window.location.replace(
      `/${encodeURIComponent(company)}?session=expired`
    );
  }

  return Promise.reject(error);
}

// Instancia utilizada por api.get/api.post.
api.interceptors.response.use(
  (response) => response,
  handleUnauthorized
);

// Peticiones axios.get/axios.post de los módulos actuales.
axios.interceptors.response.use(
  (response) => response,
  handleUnauthorized
);

export async function logoutPosSession(company) {
  const session = getPosSession(company);

  if (!session?.access_token) {
    clearPosSession(company);
    return;
  }

  const token = session.access_token;

  try {
    await api.post(
      "/auth/pos/logout",
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error) {
    // Una sesión vencida ya no bloquea nuevos ingresos.
    if (error.response?.status !== 401) {
      throw error;
    }
  }

  clearPosSession(company, token);
}